<template>
    <div class="container">
        <v-title :lineage="[{'Organizations':'organizations'}]">
            {{isFormUpdating ? 'Update' : 'Add'}} Website {{isFormUpdating ? 'in' : 'to'}} {{ organizationId ||
            website.organizationId }}
            <div slot="actions">
                <router-link
                    v-if="$auth.$pages['organization-details'].isAllowed"
                    class="btn inline-s" :to="{name: 'organization-details', params:{id: website.organizationId}}">
                    Cancel
                </router-link>
                <r-button type="primary" @click="save" :loading="isLoading">Save Website</r-button>
            </div>
        </v-title>
        <v-inline-notifications
            v-if="repeatMessage"
            type="positive"
            title="Website successfully created"
            class="stack-l">
            <span slot="content">
                Would you like to add
                <r-button type="link" @click="newFromContinue">another website</r-button>
                <template v-if="$auth.$pages['organization-details'].isAllowed">&nbsp;or go back to <router-link :to="{name: 'organization-details', params: {id: website.organizationId}}">{{ website.organizationId }} details screen</router-link></template>?
            </span>
        </v-inline-notifications>
        <v-error-messages
            :has-frontend-errors="$v.website.$error"
            :server-error="serverError"
            class="stack-l">
            <li class="list-item" v-if="!$v.website.name.required">Website name is required</li>
            <li class="list-item" v-if="!$v.website.url.required">Domain is required</li>
            <li class="list-item" v-if="!$v.website.url.url">Domain url is invalid.</li>
            <li class="list-item" v-if="!$v.website.servicePhone.required">Customer service phone is required</li>
            <li class="list-item" v-if="!$v.website.serviceEmail.required">Customer service email is invalid</li>
            <li class="list-item" v-if="!$v.website.serviceEmail.email">Customer service email is invalid</li>
        </v-error-messages>
        <div class="grid">
            <div class="grid-item grid-item-secondary">
                <div class="tile tile-description">
                    <div class="tile-title">
                        <h2>Website Information</h2>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-primary">
                <div class="tile">
                    <div class="tile-content">
                        <div class="grid-content">
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    auto-focus
                                    label="Website Name"
                                    v-model="website.name"
                                    :validate="$v.website.name"
                                    class="slug-source"
                                />
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    v-model="website.id"
                                    label="Website ID"
                                    help-text="ID used to uniquely identify this website. If not provided, it will be generated automatically."
                                    :disabled="isFormUpdating"
                                    class="slug-target"
                                />
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="Domain"
                                    v-model="website.url"
                                    help-text="Ex: https://www.mywebsite.com"
                                    :validate="$v.website.url"
                                />
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="Customer Service Phone"
                                    v-model="website.servicePhone"
                                    :validate="$v.website.servicePhone"
                                />
                            </div>
                            <div class="grid-item grid-item-half grid-item-break-half">
                                <r-input
                                    label="Customer Service Email"
                                    v-model="website.serviceEmail"
                                    :validate="$v.website.serviceEmail"
                                />
                            </div>
                            <v-custom-fields-manager
                                resource-type="websites"
                                v-model="website.customFields"/>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="divider"></div>
        <div class="grid-fitted stack-l">
            <div class="grid-item text-right equal-widths-elements-mobile">
                <router-link
                    v-if="$auth.$pages['organization-details'].isAllowed"
                    class="btn inline-s" :to="{name: 'organization-details', params:{id: website.organizationId}}">
                    Cancel
                </router-link>
                <r-button type="primary" @click="save" :loading="isLoading">Save Website</r-button>
            </div>
        </div>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vErrorMessages, vIcon, vIconSprites, vSelect,
    } from '@/components/form';
    import {email, required, url} from 'vuelidate/lib/validators';
    import vInlineNotifications from '@/components/v-inline-notifications';
    import FormLoaderMixin from '@/mixins/form-loader-mixin';
    import sluggifyMixin from '@/mixins/sluggify-mixin';
    import WebsiteModel from '@/common/models/website-model';
    import vCustomFieldsManager from '@/components/custom-fields/v-custom-fields-manager';

    export default {
        name: 'website-form',
        mixins: [
            FormLoaderMixin,
            sluggifyMixin({
                target: 'website.id',
                source: 'website.name',
                entityCallback(id) {
                    return this.$api(({websites}) => websites.get({id}));
                },
            }),
        ],
        components: {
            vTitle,
            vIconSprites,
            vIcon,
            vSelect,
            vErrorMessages,
            vInlineNotifications,
            vCustomFieldsManager,
        },
        props: {
            id: {
                type: [String, Number],
                default: null,
            },
            continue: {
                type: String,
                default: null,
            },
            organizationId: {
                type: [String, Number],
                default: null,
            },
        },
        data() {
            return {
                website: new WebsiteModel({
                    id: this.id || null,
                    organizationId: this.organizationId || null,
                }),
            };
        },
        validations: {
            website: {
                name: {required},
                url: {required, url},
                servicePhone: {required},
                serviceEmail: {required, email},
            },
        },
        computed: {
            repeatMessage() {
                return this.continue !== null;
            },
        },
        methods: {
            newFromContinue() {
                this.$v.$reset();
                this.$router.push({name: 'add-website', params: {organizationId: this.website.organizationId}});
                this.website = new WebsiteModel({organizationId: this.website.organizationId});
            },
            async save() {
                this.serverError = null;
                this.$v.$touch();
                if (!this.$v.website.$error) {
                    this.isLoading = true;
                    try {
                        const params = {
                            data: this.website.getPayload(),
                        };
                        if (this.website.id) {
                            params.id = this.website.id;
                        }
                        const method = this.isFormUpdating ? 'update' : 'create';
                        const website = await this.$api(({websites}) => websites[method](params));
                        window.scrollTo(0, 0);
                        if (!this.continue && !this.isFormUpdating) {
                            this.$router.push({
                                name: 'update-website',
                                params: {id: website.fields.id, continue: 'continue'},
                            });
                            this.website = new WebsiteModel(website.fields);
                        } else {
                            this.$toast.positive('Website successfully updated');
                            this.$router.push({name: 'update-website', params: {id: website.fields.id}});
                        }
                        this.isLoading = false;
                    } catch (error) {
                        window.scrollTo(0, 0);
                        this.serverError = error;
                    } finally {
                        this.isLoading = false;
                    }
                }
            },
            async fetchData() {
                if (!this.isFormUpdating) {
                    return;
                }
                try {
                    const {fields} = await this.$api(({websites}) => websites.get({id: this.id}));
                    this.website = new WebsiteModel(fields);
                    this.isLoading = false;
                } catch (error) {
                    this.serverError = error;
                } finally {
                    this.isLoading = false;
                }
            },
        },
        mounted() {
            this.website.organizationId = this.organizationId;
        },
    };
</script>
